import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { VerifiedBadge } from '../components/app/VerifiedBadge';
import { Compass, Search, MessageCircle, UserPlus, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

function UserCard({ u, me, onChanged }) {
  const navigate = useNavigate();
  const { t } = useApp();
  const [following, setFollowing] = useState((u.followers || []).includes(me?.id));
  const [busy, setBusy] = useState(false);

  const toggleFollow = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post(`/users/${u.id}/follow`);
      setFollowing(res.data.following);
      toast.success(res.data.following ? `${t('follow')}: @${u.username}` : `${t('unfollow')}: @${u.username}`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="discover-user-card" className="bg-card rounded-xl border border-border p-4 elev-1 flex flex-col items-center text-center hover:border-primary/40 transition-colors">
      <Avatar className="w-16 h-16 cursor-pointer mb-2" onClick={() => navigate(`/profile/${u.id}`)}>
        <AvatarImage src={u.avatar} />
        <AvatarFallback className="text-lg">{u.username?.[0]?.toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex items-center gap-1 cursor-pointer" onClick={() => navigate(`/profile/${u.id}`)}>
        <p className="text-sm font-semibold truncate max-w-[140px]">{u.full_name}</p>
        {u.verified && <VerifiedBadge size={14} />}
      </div>
      <p className="text-xs text-muted-foreground">@{u.username}</p>
      {u.bio && <p className="text-[11px] text-muted-foreground mt-1.5 line-clamp-2 min-h-[28px]">{u.bio}</p>}
      <p className="text-[11px] text-muted-foreground mt-1">
        {(u.follower_count ?? u.followers?.length ?? 0)} {t('followers')} · {u.xp || 0} XP
      </p>
      <div className="flex gap-2 mt-3 w-full">
        <Button
          data-testid="discover-follow-button"
          size="sm"
          onClick={toggleFollow}
          disabled={busy}
          variant={following ? 'secondary' : 'default'}
          className="flex-1 h-8 text-xs font-semibold"
        >
          {following ? <UserCheck size={13} className="me-1" /> : <UserPlus size={13} className="me-1" />}
          {following ? t('unfollow') : t('follow')}
        </Button>
        <Button data-testid="discover-dm-button" size="sm" variant="outline" className="h-8 px-2.5" onClick={() => navigate(`/messages/${u.id}`)}>
          <MessageCircle size={13} />
        </Button>
      </div>
    </div>
  );
}

export default function DiscoverPage() {
  const { user, t } = useApp();
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const tm = setTimeout(() => {
      const req = q.trim()
        ? api.get(`/users/search?q=${encodeURIComponent(q)}`)
        : api.get('/users/suggestions');
      req
        .then((r) => setUsers(r.data.filter((u) => u.id !== user?.id)))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(tm);
  }, [q, user?.id]);

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4">
      <div className="flex items-center gap-2 mb-1">
        <Compass size={20} className="text-primary" />
        <h1 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{t('discover')}</h1>
      </div>
      <p className="text-xs text-muted-foreground mb-4">{t('suggestedForYou')}</p>

      <div className="relative mb-5">
        <Search size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="discover-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`${t('search')}...`}
          className="ps-9"
        />
      </div>

      {loading ? (
        <div className="py-12 text-center text-muted-foreground text-sm">{t('loading')}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="discover-users-grid">
          {users.map((u) => (
            <UserCard key={u.id} u={u} me={user} />
          ))}
        </div>
      )}
      {!loading && users.length === 0 && (
        <p className="text-center text-sm text-muted-foreground py-10">No users found</p>
      )}
    </div>
  );
}
